import React, { useState, useEffect, useCallback } from 'react';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Shield, Save, Plus, X, Download, Loader2, Layers, Umbrella } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };
const fmt$ = v => { if (v == null) return '$0'; if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`; if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(0)}K`; return `$${Math.round(v).toLocaleString()}`; };

export default function ReinsurancePage() {
  const api = useApi();
  const { user } = useAuth();
  const canEdit = ['mga_admin', 'platform_owner'].includes(user?.role);
  const [summary, setSummary] = useState(null);
  const [prog, setProg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get('/reinsurance/summary')
      .then(r => { setSummary(r.data); setProg(JSON.parse(JSON.stringify(r.data.program))); })
      .catch(err => toast.error(err?.response?.data?.detail || 'Failed to load reinsurance program'));
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/reinsurance/program', {
        treaty_year: prog.treaty_year, inception: prog.inception, expiry: prog.expiry,
        quota_share: prog.quota_share, xol_layers: prog.xol_layers, reinsurer_panel: prog.reinsurer_panel,
      });
      toast.success('Reinsurance program updated — Finance and packs now use the new structure');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save program');
    }
    setSaving(false);
  };

  if (!summary || !prog) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-md" />)}</div>;
  const k = summary.kpis;
  const qs = prog.quota_share;
  const panelTotal = Math.round((prog.reinsurer_panel || []).reduce((s, r) => s + (parseFloat(r.share_pct) || 0), 0) * 10) / 10;

  return (
    <div className="space-y-4" data-testid="reinsurance-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Umbrella className="w-4 h-4" style={{ color: C.blue }} />
          <div>
            <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Reinsurance Program — Treaty Year {prog.treaty_year}</h3>
            <p className="text-[11px]" style={{ color: C.slate }}>{prog.inception} → {prog.expiry} · Finance P&L, loss ratios and the Reinsurer Pack read from this live structure</p>
          </div>
        </div>
        {canEdit && (
          <button onClick={save} disabled={saving} data-testid="reinsurance-save-btn"
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-md text-xs font-semibold text-white disabled:opacity-50 transition-opacity hover:opacity-90"
            style={{ background: '#FF9933' }}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Save Program
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPI label="Gross GWP" value={fmt$(k.gross_gwp)} color={C.navy} testid="re-kpi-gross" />
        <KPI label="Ceded GWP" value={fmt$(k.ceded_gwp)} sub={`${k.effective_cession_pct}% effective`} color={C.blue} testid="re-kpi-ceded" />
        <KPI label="Net Retained GWP" value={fmt$(k.net_retained_gwp)} color={C.green} testid="re-kpi-retained" />
        <KPI label="Ceding Commission" value={fmt$(k.ceding_commission)} color={C.teal} testid="re-kpi-commission" />
        <KPI label="XoL Spend vs Recoveries" value={`${fmt$(k.xol_premium_spend)} / ${fmt$(k.xol_recoveries)}`}
          sub={`${k.xol_recovery_ratio_pct}% recovery ratio · ${k.xol_claims} claim${k.xol_claims === 1 ? '' : 's'} pierce`}
          color={k.xol_recoveries > 0 ? C.amber : C.slate} testid="re-kpi-xol" />
        <KPI label="Largest Retained Loss" value={fmt$(k.largest_retained_loss)} color={C.slate} testid="re-kpi-largest" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Quota share */}
        <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="re-quota-card">
          <SectionHead icon={Shield} title="Quota Share Cessions" />
          <div className="flex flex-wrap gap-4 mt-2 mb-3">
            <LabeledPct label="Default cession" value={qs.default_cession} disabled={!canEdit}
              testid="re-default-cession"
              onChange={v => setProg({ ...prog, quota_share: { ...qs, default_cession: v } })} />
            <LabeledPct label="Ceding commission" value={qs.ceding_commission} disabled={!canEdit}
              testid="re-ceding-commission"
              onChange={v => setProg({ ...prog, quota_share: { ...qs, ceding_commission: v } })} />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {summary.class_cessions.map(cc => (
              <div key={cc.class} className="flex items-center gap-3 py-1.5 border-b text-xs" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
                <span className="flex-1 truncate" style={{ color: C.navy }}>{cc.class}</span>
                <span className="font-mono text-[10px]" style={{ color: C.slate }}>{fmt$(cc.gwp)} · {cc.policies}</span>
                <PctInput value={qs.class_cessions?.[cc.class] ?? qs.default_cession} disabled={!canEdit}
                  testid={`re-cession-${cc.class.replace(/\s/g, '-')}`}
                  onChange={v => setProg({ ...prog, quota_share: { ...qs, class_cessions: { ...(qs.class_cessions || {}), [cc.class]: v } } })} />
                <span className="font-mono text-[10px] w-16 text-right" style={{ color: C.blue }}>{fmt$(cc.ceded)}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* XoL layers */}
        <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="re-xol-card">
          <SectionHead icon={Layers} title="Excess of Loss Layers (per risk, net of QS)" action={canEdit && (
            <AddBtn testid="re-xol-add" onClick={() => setProg({
              ...prog, xol_layers: [...(prog.xol_layers || []), {
                layer_id: `L${(prog.xol_layers || []).length + 1}-${Date.now().toString(36).slice(-4)}`,
                name: `Layer ${(prog.xol_layers || []).length + 1}`, attachment: 1000000, limit: 1000000, premium_rate_pct: 1.0
              }]
            })} />
          )} />
          <div className="grid grid-cols-12 gap-2 mt-2 text-[9px] uppercase tracking-wider font-semibold" style={{ color: C.slate }}>
            <span className="col-span-4">Layer</span><span className="col-span-3 text-right">Attachment</span>
            <span className="col-span-3 text-right">Limit</span><span className="col-span-1 text-right">Rate%</span><span />
          </div>
          {(prog.xol_layers || []).map((l, i) => {
            const live = summary.layers.find(x => x.layer_id === l.layer_id);
            return (
              <div key={l.layer_id || i} className="grid grid-cols-12 gap-2 items-center py-1.5 border-b" style={{ borderColor: 'rgba(136,150,168,0.06)' }} data-testid={`re-xol-row-${i}`}>
                <input className="col-span-4 px-2 py-1 text-xs rounded border" disabled={!canEdit}
                  style={{ borderColor: 'rgba(136,150,168,0.25)', color: C.navy }} value={l.name || ''}
                  onChange={e => updLayer(prog, setProg, i, { name: e.target.value })} />
                <NumInput className="col-span-3" value={l.attachment} disabled={!canEdit} onChange={v => updLayer(prog, setProg, i, { attachment: v })} />
                <NumInput className="col-span-3" value={l.limit} disabled={!canEdit} onChange={v => updLayer(prog, setProg, i, { limit: v })} />
                <NumInput className="col-span-1" value={l.premium_rate_pct} step="0.1" disabled={!canEdit} onChange={v => updLayer(prog, setProg, i, { premium_rate_pct: v })} />
                <div className="col-span-1 flex justify-end">
                  {canEdit && <button onClick={() => setProg({ ...prog, xol_layers: prog.xol_layers.filter((_, j) => j !== i) })}
                    data-testid={`re-xol-delete-${i}`} className="p-1 rounded hover:bg-red-50"><X className="w-3 h-3" style={{ color: C.red }} /></button>}
                </div>
                {live && (
                  <p className="col-span-12 text-[10px] -mt-1" style={{ color: C.slate }} data-testid={`re-xol-cost-${i}`}>
                    Annual cost {fmt$(live.annual_premium_cost)} <span style={{ color: '#B8C2CC' }}>({l.premium_rate_pct}% on retained GNPI)</span>
                    {live.recoveries > 0 && <span style={{ color: '#B27516' }}> · Recoveries to date {fmt$(live.recoveries)} ({live.annual_premium_cost > 0 ? Math.round(live.recoveries / live.annual_premium_cost * 100) : 0}% of cost)</span>}
                  </p>
                )}
              </div>
            );
          })}
          {(prog.xol_layers || []).length === 0 && <p className="text-xs py-3 text-center" style={{ color: C.slate }}>No XoL protection — retained losses are unlimited above the quota share.</p>}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Panel */}
        <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="re-panel-card">
          <SectionHead icon={Shield} title={`Reinsurer Panel${panelTotal !== 100 ? ` — shares total ${panelTotal}%` : ''}`}
            warn={panelTotal !== 100}
            action={canEdit && <AddBtn testid="re-panel-add" onClick={() => setProg({ ...prog, reinsurer_panel: [...(prog.reinsurer_panel || []), { name: '', share_pct: 0, rating: 'A' }] })} />} />
          {(prog.reinsurer_panel || []).map((r, i) => (
            <div key={i} className="flex items-center gap-2 py-1.5 border-b" style={{ borderColor: 'rgba(136,150,168,0.06)' }} data-testid={`re-panel-row-${i}`}>
              <input className="flex-1 px-2 py-1 text-xs rounded border" placeholder="Reinsurer name" disabled={!canEdit}
                style={{ borderColor: 'rgba(136,150,168,0.25)', color: C.navy }} value={r.name || ''}
                onChange={e => updPanel(prog, setProg, i, { name: e.target.value })} />
              <input className="w-20 px-2 py-1 text-xs rounded border text-right font-mono" disabled={!canEdit}
                style={{ borderColor: 'rgba(136,150,168,0.25)', color: C.navy }} type="number" step="1" value={r.share_pct}
                onChange={e => updPanel(prog, setProg, i, { share_pct: parseFloat(e.target.value) || 0 })} />
              <span className="text-[10px]" style={{ color: C.slate }}>%</span>
              <input className="w-14 px-2 py-1 text-xs rounded border" placeholder="AA-" disabled={!canEdit}
                style={{ borderColor: 'rgba(136,150,168,0.25)', color: C.navy }} value={r.rating || ''}
                onChange={e => updPanel(prog, setProg, i, { rating: e.target.value })} />
              {canEdit && <button onClick={() => setProg({ ...prog, reinsurer_panel: prog.reinsurer_panel.filter((_, j) => j !== i) })}
                data-testid={`re-panel-delete-${i}`} className="p-1 rounded hover:bg-red-50"><X className="w-3 h-3" style={{ color: C.red }} /></button>}
            </div>
          ))}
        </Card>

        {/* Bordereaux */}
        <BordereauCard />

        {/* Treaty Statements */}
        <TreatyStatementsCard canEdit={canEdit} />

        {/* Facultative Placement Tracker */}
        <FacPlacementCard canEdit={canEdit} />
      </div>
    </div>
  );
}

function updLayer(prog, setProg, i, patch) {
  const layers = prog.xol_layers.map((l, j) => j === i ? { ...l, ...patch } : l);
  setProg({ ...prog, xol_layers: layers });
}
function updPanel(prog, setProg, i, patch) {
  const panel = prog.reinsurer_panel.map((r, j) => j === i ? { ...r, ...patch } : r);
  setProg({ ...prog, reinsurer_panel: panel });
}

function BordereauCard() {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [btype, setBtype] = useState('premium');
  const API_BASE = process.env.REACT_APP_BACKEND_URL;
  const url = fmt => `${API_BASE}/api/reinsurance/bordereau?btype=${btype}&month=${month}&fmt=${fmt}`;
  return (
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="re-bordereau-card">
      <SectionHead icon={Download} title="Cession Bordereaux" />
      <p className="text-[11px] mt-1 mb-3" style={{ color: C.slate }}>Monthly premium and claims bordereaux for the reinsurer panel — line-level cessions and recoveries under the live program.</p>
      <div className="flex flex-wrap items-center gap-2">
        <select value={btype} onChange={e => setBtype(e.target.value)} data-testid="re-bordereau-type"
          className="px-2 py-1.5 text-xs rounded border" style={{ borderColor: 'rgba(136,150,168,0.25)', color: C.navy }}>
          <option value="premium">Premium bordereau</option>
          <option value="claims">Claims bordereau</option>
        </select>
        <input type="month" value={month} onChange={e => setMonth(e.target.value)} data-testid="re-bordereau-month"
          className="px-2 py-1 text-xs rounded border" style={{ borderColor: 'rgba(136,150,168,0.25)', color: C.navy }} />
        <a href={url('csv')} target="_blank" rel="noreferrer" data-testid="re-bordereau-csv"
          className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[10px] font-semibold text-white transition-opacity hover:opacity-90"
          style={{ background: C.blue }}><Download className="w-3 h-3" />CSV</a>
        <a href={url('pdf')} target="_blank" rel="noreferrer" data-testid="re-bordereau-pdf"
          className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[10px] font-semibold transition-colors hover:bg-blue-50"
          style={{ border: `1px solid ${C.blue}`, color: C.blue }}><Download className="w-3 h-3" />PDF</a>
      </div>
    </Card>
  );
}

function TreatyStatementsCard({ canEdit }) {
  const api = useApi();
  const now = new Date();
  const defQ = `${now.getFullYear()}-Q${Math.floor(now.getMonth() / 3) + 1}`;
  const [quarter, setQuarter] = useState(defQ);
  const [stmts, setStmts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  const load = useCallback(() => {
    api.get('/reinsurance/statements').then(r => setStmts(r.data.statements)).catch(() => setStmts([]));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post('/reinsurance/statements/generate', { quarter });
      toast.success(`Statement ${quarter} generated — net ${fmt$(Math.abs(r.data.data.net_balance))} ${r.data.data.net_balance >= 0 ? 'payable to' : 'receivable from'} reinsurers`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not generate statement'); }
    setBusy(false);
  };

  const act = async (s, action) => {
    setBusy(true);
    try {
      const r = await api.post(`/reinsurance/statements/${s.statement_id}/${action}`);
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    setBusy(false);
  };

  const dispatch = async (s) => {
    setBusy(true);
    try {
      const r = await api.post(`/reinsurance/statements/${s.statement_id}/dispatch`);
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Dispatch failed'); }
    setBusy(false);
  };

  const STATUS = { draft: { color: C.amber, label: 'Draft' }, finalised: { color: C.blue, label: 'Finalised' }, settled: { color: C.green, label: 'Settled' } };
  const qOpts = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 1; y--) for (let q = 4; q >= 1; q--) qOpts.push(`${y}-Q${q}`);

  return (
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="re-statements-card">
      <SectionHead icon={Layers} title="Treaty Statements" />
      <p className="text-[11px] mt-1 mb-3" style={{ color: C.slate }}>Quarterly QS + XoL accounting — ceded premium less commission and paid recoveries, split across the panel. Draft → Finalise (locks figures) → Settle.</p>
      {canEdit && (
        <div className="flex items-center gap-2 mb-3">
          <select value={quarter} onChange={e => setQuarter(e.target.value)} data-testid="re-stmt-quarter"
            className="px-2 py-1.5 text-xs rounded border" style={{ borderColor: 'rgba(136,150,168,0.25)', color: C.navy }}>
            {qOpts.map(q => <option key={q} value={q}>{q}</option>)}
          </select>
          <button onClick={generate} disabled={busy} data-testid="re-stmt-generate"
            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[10px] font-semibold text-white disabled:opacity-50 transition-opacity hover:opacity-90"
            style={{ background: C.blue }}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}Generate
          </button>
        </div>
      )}
      {!stmts ? <Skeleton className="h-16 rounded-md" /> : stmts.length === 0 ? (
        <p className="text-xs" style={{ color: C.slate }}>No statements yet — generate the current quarter to start the treaty ledger.</p>
      ) : (
        <div className="space-y-2">
          {stmts.map(s => {
            const st = STATUS[s.status] || STATUS.draft;
            const d = s.data;
            const isOpen = open === s.statement_id;
            return (
              <div key={s.statement_id} className="rounded-md" style={{ border: '1px solid rgba(136,150,168,0.15)' }} data-testid={`re-stmt-${s.quarter}`}>
                <button onClick={() => setOpen(isOpen ? null : s.statement_id)} className="w-full flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5 text-left hover:bg-[#F5F7FA]">
                  <span className="font-mono text-xs font-semibold" style={{ color: C.navy }}>{s.quarter}</span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: `${st.color}12`, color: st.color }}>{st.label}</span>
                  <span className="text-[11px]" style={{ color: C.slate }}>Ceded {fmt$(d.qs.ceded_premium)} · Comm {fmt$(d.qs.ceding_commission)} · Recoveries {fmt$(d.qs.paid_claim_recoveries + d.xol.recoveries)}</span>
                  <span className="ml-auto font-mono text-xs font-semibold" style={{ color: d.net_balance >= 0 ? C.green : C.red }}>
                    {fmt$(Math.abs(d.net_balance))} {d.net_balance >= 0 ? 'payable' : 'receivable'}
                  </span>
                </button>
                {isOpen && (
                  <div className="px-3 pb-3 space-y-2">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <MiniStat label="GWP written" value={fmt$(d.qs.gross_written_premium)} color={C.navy} />
                      <MiniStat label={`QS balance`} value={fmt$(d.qs.balance_due_reinsurer)} color={C.blue} />
                      <MiniStat label="XoL instalment" value={fmt$(d.xol.quarterly_premium_instalment)} color={C.amber} />
                      <MiniStat label="XoL balance" value={fmt$(d.xol.balance_due_reinsurer)} color={C.blue} />
                    </div>
                    <div className="text-[11px] space-y-0.5" style={{ color: C.slate }}>
                      {d.panel_split.map(p => (
                        <p key={p.name}><span className="font-medium" style={{ color: C.navy }}>{p.name}</span> ({p.share_pct}%) — net {fmt$(Math.abs(p.net_balance))} {p.net_balance >= 0 ? 'payable' : 'receivable'}</p>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <a href={`${API_BASE}/api/reinsurance/statements/${s.statement_id}/pdf`} target="_blank" rel="noreferrer"
                        className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[10px] font-semibold transition-colors hover:bg-blue-50"
                        style={{ border: `1px solid ${C.blue}`, color: C.blue }} data-testid={`re-stmt-pdf-${s.quarter}`}>
                        <Download className="w-3 h-3" />PDF
                      </a>
                      {canEdit && s.status === 'draft' && <>
                        <button onClick={generate2(s)} disabled={busy} data-testid={`re-stmt-regen-${s.quarter}`}
                          className="px-3 py-1.5 rounded-md text-[10px] font-semibold disabled:opacity-50"
                          style={{ border: '1px solid rgba(136,150,168,0.3)', color: C.slate }}>Regenerate</button>
                        <button onClick={() => act(s, 'finalise')} disabled={busy} data-testid={`re-stmt-finalise-${s.quarter}`}
                          className="px-3 py-1.5 rounded-md text-[10px] font-semibold text-white disabled:opacity-50"
                          style={{ background: C.blue }}>Finalise</button>
                      </>}
                      {canEdit && (s.status === 'finalised' || s.status === 'settled') && (
                        <button onClick={() => dispatch(s)} disabled={busy} data-testid={`re-stmt-dispatch-${s.quarter}`}
                          className="px-3 py-1.5 rounded-md text-[10px] font-semibold text-white disabled:opacity-50"
                          style={{ background: '#FF9933' }}>Email Panel</button>
                      )}
                      {canEdit && s.status === 'finalised' && (
                        <button onClick={() => act(s, 'settle')} disabled={busy} data-testid={`re-stmt-settle-${s.quarter}`}
                          className="px-3 py-1.5 rounded-md text-[10px] font-semibold text-white disabled:opacity-50"
                          style={{ background: C.green }}>Mark Settled</button>
                      )}
                      {s.dispatched_at && <span className="text-[10px]" style={{ color: C.slate }}>Emailed to {s.dispatched_to?.length || 0} contact{(s.dispatched_to?.length || 0) === 1 ? '' : 's'} {s.dispatched_at.slice(0, 10)}</span>}
                      {s.settled_at && <span className="text-[10px]" style={{ color: C.slate }}>Settled {s.settled_at.slice(0, 10)} by {s.settled_by}</span>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );

  function generate2(s) {
    return async () => {
      setBusy(true);
      try {
        await api.post('/reinsurance/statements/generate', { quarter: s.quarter });
        toast.success(`Statement ${s.quarter} regenerated with latest figures`);
        load();
      } catch (e) { toast.error(e?.response?.data?.detail || 'Could not regenerate'); }
      setBusy(false);
    };
  }
}

function FacPlacementCard({ canEdit }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('pending_placement');
  const [placing, setPlacing] = useState(null);
  const [reinsurer, setReinsurer] = useState('');
  const [facPremium, setFacPremium] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get('/reinsurance/fac-referrals').then(r => setData(r.data)).catch(() => setData({ referrals: [], counts: {} }));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const place = async (p) => {
    if (!reinsurer.trim()) { toast.error('Enter the reinsurer name'); return; }
    setBusy(true);
    try {
      const body = { reinsurer: reinsurer.trim() };
      if (facPremium) body.fac_premium = facPremium;
      const r = await api.post(`/reinsurance/fac-referrals/${p.policy_id}/place`, body);
      toast.success(r.data.message);
      setPlacing(null); setReinsurer(''); setFacPremium('');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Placement failed'); }
    setBusy(false);
  };

  const decline = async (p) => {
    setBusy(true);
    try {
      const r = await api.post(`/reinsurance/fac-referrals/${p.policy_id}/decline`, {});
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Decline failed'); }
    setBusy(false);
  };

  if (!data) return <Skeleton className="h-32 rounded-md" />;
  const ST = { pending_placement: { color: C.amber, label: 'Pending' }, placed: { color: C.green, label: 'Placed' }, declined: { color: C.red, label: 'Declined' } };
  const rows = data.referrals.filter(r => !filter || r.fac_referral.status === filter);

  return (
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="fac-tracker-card">
      <SectionHead icon={Umbrella} title="Facultative Placements" />
      <p className="text-[11px] mt-1 mb-3" style={{ color: C.slate }}>
        Risks referred out of treaty accumulations (Portfolio → Geo Exposure). Work each one — place it with a fac market or decline and keep it on treaty.
        {data.pending_tiv > 0 && <> Pending TIV: <b style={{ color: C.amber }}>${Math.round(data.pending_tiv / 1e6)}M</b>.</>}
        {data.expiring_placements > 0 && <> <b style={{ color: C.red }} data-testid="fac-expiring-banner">{data.expiring_placements} placement{data.expiring_placements !== 1 ? 's' : ''} expiring within 60 days — renew the fac or the exposure returns to treaty.</b></>}
      </p>
      <div className="flex gap-1.5 mb-3">
        {Object.entries(ST).map(([k, v]) => (
          <button key={k} onClick={() => setFilter(filter === k ? '' : k)} data-testid={`fac-filter-${k}`} aria-pressed={filter === k}
            className="px-2.5 py-1 rounded-full text-[10px] font-semibold"
            style={{ background: filter === k ? v.color : `${v.color}14`, color: filter === k ? '#fff' : v.color }}>
            {v.label} {data.counts[k] || 0}
          </button>
        ))}
      </div>
      {rows.length === 0 ? (
        <p className="text-xs" style={{ color: C.slate }}>Nothing here — refer risks from the Geo Exposure breach drilldown.</p>
      ) : (
        <div className="space-y-1.5 max-h-[340px] overflow-y-auto">
          {rows.map(p => {
            const fr = p.fac_referral;
            const st = ST[fr.status] || ST.pending_placement;
            return (
              <div key={p.policy_id} className="rounded-md px-3 py-2" style={{ border: '1px solid rgba(136,150,168,0.15)' }} data-testid={`fac-row-${p.policy_number}`}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-mono text-[11px]" style={{ color: C.navy }}>{p.policy_number}</span>
                  <span className="text-xs font-medium truncate max-w-[180px]" style={{ color: C.navy }}>{p.business_name}</span>
                  <span className="text-[11px]" style={{ color: C.slate }}>TIV ${Math.round((p.sum_insured || 0) / 1e6 * 10) / 10}M · {fr.reason}</span>
                  {fr.carried_from && (
                    <span className="px-2 py-0.5 rounded-sm text-[9px] font-bold uppercase" data-testid={`fac-carried-${p.policy_number}`}
                      style={{ background: 'rgba(26,76,124,0.08)', color: C.blue }}>Carried from {fr.carried_from}</span>
                  )}
                  <span className="ml-auto px-2 py-0.5 rounded-full text-[9px] font-bold uppercase" style={{ background: `${st.color}14`, color: st.color }}>{st.label}</span>
                  {fr.status === 'placed' && <span className="text-[10px]" style={{ color: C.slate }}>{fr.reinsurer}{fr.fac_premium ? ` · $${Number(fr.fac_premium).toLocaleString()}` : ''}</span>}
                  {fr.status === 'placed' && p.days_to_expiry != null && p.days_to_expiry <= 60 && (
                    <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold" data-testid={`fac-expiry-warn-${p.policy_number}`}
                      style={{ background: p.days_to_expiry <= 14 ? 'rgba(226,75,74,0.1)' : 'rgba(232,160,32,0.12)', color: p.days_to_expiry <= 14 ? C.red : '#B27516' }}>
                      ⚠ Policy renews in {Math.max(p.days_to_expiry, 0)}d{p.renewal_status ? ` (renewal ${p.renewal_status.replace(/_/g, ' ')})` : ''} — renew the fac placement
                    </span>
                  )}
                  {canEdit && fr.status === 'pending_placement' && (
                    <span className="flex gap-1.5">
                      <button onClick={() => { setPlacing(placing === p.policy_id ? null : p.policy_id); setReinsurer(''); setFacPremium(''); }}
                        data-testid={`fac-place-btn-${p.policy_number}`}
                        className="px-2.5 py-1 rounded-md text-[10px] font-semibold text-white" style={{ background: C.green }}>Placed</button>
                      <button onClick={() => decline(p)} disabled={busy} data-testid={`fac-decline-btn-${p.policy_number}`}
                        className="px-2.5 py-1 rounded-md text-[10px] font-semibold disabled:opacity-50" style={{ border: `1px solid ${C.red}50`, color: C.red }}>Decline</button>
                    </span>
                  )}
                </div>
                {placing === p.policy_id && (
                  <div className="flex flex-wrap items-center gap-2 mt-2 pt-2" style={{ borderTop: '1px dashed rgba(136,150,168,0.2)' }}>
                    <input value={reinsurer} onChange={e => setReinsurer(e.target.value)} placeholder="Reinsurer (e.g. Lloyd's Syndicate 2003)"
                      data-testid={`fac-reinsurer-input-${p.policy_number}`}
                      className="px-2 py-1.5 text-xs rounded border flex-1 min-w-[180px]" style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }} />
                    <input value={facPremium} onChange={e => setFacPremium(e.target.value)} placeholder="Fac premium (optional)" type="number"
                      data-testid={`fac-premium-input-${p.policy_number}`}
                      className="px-2 py-1.5 text-xs rounded border w-40" style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }} />
                    <button onClick={() => place(p)} disabled={busy} data-testid={`fac-confirm-place-${p.policy_number}`}
                      className="px-3 py-1.5 rounded-md text-[10px] font-semibold text-white disabled:opacity-50" style={{ background: C.green }}>
                      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Confirm placement'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

export function ClaimReinsurancePanel({ claimId }) {
  const api = useApi();
  const [rec, setRec] = useState(null);
  useEffect(() => {
    api.get(`/reinsurance/claim/${claimId}`).then(r => setRec(r.data)).catch(() => setRec(null));
  }, [api, claimId]);
  if (!rec || rec.gross_incurred <= 0) return null;
  return (
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(26,76,124,0.18)', background: 'rgba(26,76,124,0.02)' }} data-testid="claim-reinsurance-panel">
      <div className="flex items-center gap-2 mb-2">
        <Umbrella className="w-3.5 h-3.5" style={{ color: C.blue }} />
        <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Reinsurance Recovery</h3>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <MiniStat label="Gross Incurred" value={fmt$(rec.gross_incurred)} color={C.navy} />
        <MiniStat label={`QS Recovery (${rec.cession_pct}%)`} value={fmt$(rec.qs_recovery)} color={C.blue} />
        <MiniStat label="XoL Recovery" value={fmt$(rec.xol_recovery_total)} color={rec.xol_recovery_total > 0 ? C.amber : C.slate} />
        <MiniStat label="Net Retained" value={fmt$(rec.net_retained)} color={C.green} />
      </div>
      {rec.xol_recoveries?.length > 0 && (
        <p className="text-[10px] mt-2" style={{ color: '#B27516' }}>
          Pierces: {rec.xol_recoveries.map(x => `${x.name} ${fmt$(x.recovery)}`).join(' · ')}
        </p>
      )}
    </Card>
  );
}

function MiniStat({ label, value, color }) {
  return (<div>
    <p className="text-[9px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{label}</p>
    <p className="text-sm font-semibold font-mono mt-0.5" style={{ color }}>{value}</p>
  </div>);
}

function KPI({ label, value, sub, color, testid }) {
  return (
    <Card className="px-3 py-2.5 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid={testid}>
      <p className="text-[9px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{label}</p>
      <p className="text-base font-semibold mt-0.5 font-mono" style={{ color }}>{value}</p>
      {sub && <p className="text-[9px] font-mono" style={{ color: C.slate }}>{sub}</p>}
    </Card>
  );
}

function SectionHead({ icon: Icon, title, action, warn }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Icon className="w-3.5 h-3.5" style={{ color: warn ? C.red : C.blue }} />
        <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: warn ? C.red : C.slate }}>{title}</h3>
      </div>
      {action}
    </div>
  );
}

function AddBtn({ onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors hover:bg-blue-50"
      style={{ border: '1px solid rgba(26,76,124,0.3)', color: C.blue }}>
      <Plus className="w-3 h-3" />Add
    </button>
  );
}

function LabeledPct({ label, value, onChange, disabled, testid }) {
  return (
    <label className="flex items-center gap-2 text-xs" style={{ color: C.slate }}>
      {label}
      <PctInput value={value} onChange={onChange} disabled={disabled} testid={testid} />
    </label>
  );
}

function PctInput({ value, onChange, disabled, testid }) {
  return (
    <span className="inline-flex items-center gap-1">
      <input type="number" min="0" max="90" step="0.5" disabled={disabled} data-testid={testid}
        value={Math.round((value ?? 0) * 1000) / 10}
        onChange={e => onChange(Math.max(0, Math.min(90, parseFloat(e.target.value) || 0)) / 100)}
        className="w-16 px-2 py-1 text-xs rounded border text-right font-mono disabled:opacity-60"
        style={{ borderColor: 'rgba(136,150,168,0.25)', color: C.navy }} />
      <span className="text-[10px]" style={{ color: C.slate }}>%</span>
    </span>
  );
}

function NumInput({ value, onChange, disabled, className = '', step = '1000' }) {
  return (
    <input type="number" step={step} disabled={disabled} value={value ?? 0}
      onChange={e => onChange(parseFloat(e.target.value) || 0)}
      className={`${className} px-2 py-1 text-xs rounded border text-right font-mono disabled:opacity-60`}
      style={{ borderColor: 'rgba(136,150,168,0.25)', color: C.navy }} />
  );
}
